import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/format";
import { Loader2, CheckCircle2, Smartphone, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SHIPPING = 99;
const FREE_THRESHOLD = 2999;

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [addr, setAddr] = useState({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" });
  const [note, setNote] = useState("");
  const [order, setOrder] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [paid, setPaid] = useState(false);
  const [checking, setChecking] = useState(false);
  const pollRef = useRef(null);

  const shipping = subtotal >= FREE_THRESHOLD ? 0 : SHIPPING;
  const total = subtotal + shipping;

  useEffect(() => {
    if (items.length === 0 && !order && !paid) {
      navigate("/shop");
    }
  }, [items, order, paid, navigate]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const set = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));

  const placeOrder = async () => {
    for (const [k, label] of [["full_name", "Full name"], ["phone", "Phone"], ["line1", "Address"], ["city", "City"], ["state", "State"], ["pincode", "Pincode"]]) {
      if (!addr[k]?.trim()) return toast.error(`${label} is required`);
    }
    setPlacing(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, color: i.color, qty: i.qty })),
        shipping_address: addr,
        note,
      };
      const { data } = await api.post("/checkout", payload);
      setOrder(data);
      startPolling(data.id);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setPlacing(false);
    }
  };

  const startPolling = (orderId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/${orderId}/status`);
        if (data.payment_status === "paid") {
          clearInterval(pollRef.current);
          onPaid();
        } else if (data.payment_status === "expired") {
          clearInterval(pollRef.current);
          toast.error("Payment window expired. Please try again.");
          setOrder(null);
        }
      } catch {}
    }, 4000);
  };

  const onPaid = () => {
    setPaid(true);
    clear();
    toast.success("Payment received!");
  };

  const manualCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.get(`/payments/${order.id}/status`);
      if (data.payment_status === "paid") { clearInterval(pollRef.current); onPaid(); }
      else toast.info("We haven't received your payment yet");
    } finally {
      setChecking(false);
    }
  };

  if (paid) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="checkout-success">
        <CheckCircle2 className="h-16 w-16 text-brand mx-auto" />
        <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight mt-6">Order Confirmed</h1>
        <p className="mt-3 text-sm text-zinc-400">
          Thanks {order?.user_name || "friend"} — your order <span className="font-mono text-brand">#{order?.order_no}</span> is being packed.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => navigate("/account")} data-testid="success-orders-btn" className="rounded-full bg-brand text-black px-6 py-3 text-sm font-bold uppercase tracking-wide active:scale-95 transition">View My Orders</button>
          <button onClick={() => navigate("/shop")} className="rounded-full border border-white/40 px-6 py-3 text-sm font-semibold uppercase tracking-wide hover:border-brand hover:text-brand transition">Keep Shopping</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10" data-testid="checkout-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight mb-8">Checkout</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-10">
        {/* Left */}
        <div>
          {!order ? (
            <div className="space-y-5">
              <h2 className="font-mono text-xs uppercase tracking-widest text-brand">Delivery Address</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-xs">Full Name</Label><Input data-testid="addr-name" value={addr.full_name} onChange={set("full_name")} className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
                <div><Label className="text-xs">Phone</Label><Input data-testid="addr-phone" value={addr.phone} onChange={set("phone")} className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
              </div>
              <div><Label className="text-xs">Address Line 1</Label><Input data-testid="addr-line1" value={addr.line1} onChange={set("line1")} className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
              <div><Label className="text-xs">Address Line 2 (optional)</Label><Input data-testid="addr-line2" value={addr.line2} onChange={set("line2")} className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label className="text-xs">City</Label><Input data-testid="addr-city" value={addr.city} onChange={set("city")} className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
                <div><Label className="text-xs">State</Label><Input data-testid="addr-state" value={addr.state} onChange={set("state")} className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
                <div><Label className="text-xs">Pincode</Label><Input data-testid="addr-pincode" value={addr.pincode} onChange={set("pincode")} className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
              </div>
              <div><Label className="text-xs">Order Note (optional)</Label><Input data-testid="addr-note" value={note} onChange={(e) => setNote(e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
              <button
                onClick={placeOrder}
                disabled={placing}
                data-testid="place-order-btn"
                className="w-full rounded-full bg-brand text-black py-3.5 text-sm font-bold uppercase tracking-wide hover:bg-brand-hover active:scale-95 transition disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {placing ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating UPI…</> : <>Pay {inr(total)} via UPI</>}
              </button>
            </div>
          ) : (
            <div className="space-y-6" data-testid="upi-payment-panel">
              <div className="flex items-center gap-2 text-brand">
                <Smartphone className="h-5 w-5" />
                <h2 className="font-mono text-xs uppercase tracking-widest">Scan & Pay via any UPI app</h2>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 flex flex-col items-center">
                {order.qr_url ? (
                  <img src={order.qr_url} alt="UPI QR code" className="h-56 w-56 bg-white p-2 rounded" data-testid="upi-qr" />
                ) : (
                  <div className="h-56 w-56 flex items-center justify-center text-zinc-500 text-xs font-mono">QR unavailable</div>
                )}
                <p className="mt-4 font-mono text-lg" data-testid="upi-amount">{inr(order.payable_amount || total)}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Order #{order.order_no}</p>
                {order.upi_intent && (
                  <a href={order.upi_intent} data-testid="upi-intent-link" className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2 text-xs font-semibold uppercase tracking-wide active:scale-95 transition">
                    Open in UPI App
                  </a>
                )}
              </div>
              <div className="flex items-center justify-center gap-2 text-zinc-400 text-xs font-mono">
                <Loader2 className="h-3 w-3 animate-spin" /> Waiting for payment confirmation…
              </div>
              <button onClick={manualCheck} disabled={checking} data-testid="check-payment-btn" className="w-full rounded-full bg-brand text-black py-3 text-xs font-bold uppercase tracking-wide active:scale-95 transition disabled:opacity-60">
                {checking ? "Checking…" : "I've Paid — Check Payment Status"}
              </button>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-24 h-fit border border-zinc-800 bg-zinc-950 p-6">
          <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">Order Summary</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {items.map((i, idx) => (
              <div key={idx} className="flex gap-3">
                <img src={i.image} alt={i.name} className="h-16 w-14 object-cover bg-zinc-900" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{i.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{i.size}{i.color ? ` · ${i.color}` : ""} × {i.qty}</p>
                </div>
                <span className="font-mono text-sm">{inr(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-zinc-800 space-y-2 font-mono text-sm">
            <div className="flex justify-between text-zinc-400"><span className="uppercase tracking-widest text-xs">Subtotal</span><span>{inr(subtotal)}</span></div>
            <div className="flex justify-between text-zinc-400"><span className="uppercase tracking-widest text-xs">Shipping</span><span>{shipping === 0 ? "FREE" : inr(shipping)}</span></div>
            <div className="flex justify-between text-base pt-2 border-t border-zinc-900"><span className="uppercase tracking-widest text-xs pt-1">Total</span><span data-testid="checkout-total">{inr(total)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
